<template>
  <Section class="cities">
    <div class="cities-container">
      <div class="cities-container__subheader">
        <h2 class="cities-container__subtitle">
          Les villes
          <span>où investir en {{ currentYear }}</span>
        </h2>
        <div class="actions">
          <a
            target="_blank"
            :href="analysisLink"
          >
            <uikit-button type="link">
              Proposer une analyse
            </uikit-button>
          </a>
          <uikit-dropdown
            class="sort-btn"
            :force-close-on-click="true"
          >
            <uikit-button type="link">
              Trier par: {{ sortName }}
              <template #icon>
                <uikit-arrow-up-and-down-icon class="arrow-icon" />
              </template>
              <template #right-icon>
                <uikit-chevron-icon type="down" />
              </template>
            </uikit-button>
            <template #overlay>
              <uikit-menu>
                <uikit-menu-item
                  id="pertinence"
                  :class="{ active: !sort }"
                  @click="handleSort('pertinence')"
                >
                  Pertinence
                </uikit-menu-item>
                <uikit-menu-item
                  id="population"
                  :class="{ active: sort === 'population' }"
                  @click="handleSort('population')"
                >
                  Nombre d'habitants
                </uikit-menu-item>
                <uikit-menu-item
                  id="performance"
                  :class="{ active: sort === 'performance' }"
                  @click="handleSort('performance')"
                >
                  Rendement brut
                </uikit-menu-item>
              </uikit-menu>
            </template>
          </uikit-dropdown>
        </div>
      </div>
      <div class="separator" />
      <div class="cities-container__content">
        <LazyInvest-ComponentsCityCard
          v-for="(city, index) in citiesList"
          v-if="!loading"
          :key="index"
          :city="city"
          :lazy-load-image="index !== 0"
        />
        <uikit-skeleton
          v-for="index in 8"
          v-else
          :key="'skeleton-' + index"
          class="city-skeleton"
        />
        <div class="banner-container">
          <div class="icon-mobile">
            <uikit-house-building-icon class="banner-icon" />
          </div>
          <div class="banner">
            <h3 class="banner-title">
              <uikit-house-building-icon class="banner-icon icon-desktop" />
              À la recherche du
              <span>meilleur rendement locatif ?</span>
            </h3>
            <div class="banner-description">
              Un chasseur local déniche pour vous le bien idéal dont le rendement a été vérifié ! Biens vides,
              loués, meublés ou avec travaux : nous avons la solution pour vous.
            </div>
            <a href="https://s.horiz.io/chasseimmo_home">
              <uikit-button
                type="primary"
                text-wrap
              >
                Acheter un bien rentable
              </uikit-button>
            </a>
          </div>
        </div>
      </div>
    </div>
    <OldPagination
      v-if="totalPages > 1"
      :pages="49"
      :model-value="currentPage"
    >
      <template #prevPage="{ page }">
        <NuxtLink :to="formatPagePath(page)">
          <div>&lt;</div>
        </NuxtLink>
      </template>
      <template #page="{ page }">
        <NuxtLink :to="formatPagePath(page)">
          <div>{{ page }}</div>
        </NuxtLink>
      </template>
      <template #nextPage="{ page }">
        <NuxtLink :to="formatPagePath(page)">
          <div>&gt;</div>
        </NuxtLink>
      </template>
    </OldPagination>
  </Section>
</template>

<script>
import { LEGACY_URLS } from '@/helpers/redirect'
import { currentYear } from '@/helpers/currentYear'
import {
  UikitArrowUpAndDownIcon,
  UikitButton,
  UikitChevronIcon,
  UikitDropdown,
  UikitHouseBuildingIcon,
  UikitMenu,
  UikitMenuItem,
  UikitSkeleton,
  useScreenSizeStore,
} from '@hz/ui-kit'
import { mapState } from 'pinia'

export default {
  components: {
    UikitButton,
    UikitHouseBuildingIcon,
    UikitSkeleton,
    UikitDropdown,
    UikitMenu,
    UikitMenuItem,
    UikitChevronIcon,
    UikitArrowUpAndDownIcon,
  },
  props: {
    citiesList: {
      type: Array,
      required: true,
    },
    totalCitiesCount: {
      type: Number,
      default: 0,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    sort: {
      type: String,
      default: 'pertinence',
    },
  },
  data() {
    return {
      banner: {
        calculate: {
          title: 'Comment calculer le rendement locatif ?',
          description:
            "Grâce à notre simulateur, calculez la rentabilité réelle d'un investissement locatif avant d'acheter",
          ctaText: 'Je calcule mon rendement locatif',
        },
      },
    }
  },
  computed: {
    ...mapState(useScreenSizeStore, ['isMobileWidth']),
    analysisLink() {
      return this.$config.public.rlBaseUrl + LEGACY_URLS.RL_INFORMATION_ANALYSIS
    },
    calculateLink() {
      return useUrls().onboardingFINUrl
    },
    currentYear() {
      return currentYear
    },
    totalPages() {
      return Math.ceil(this.totalCitiesCount / 20)
    },
    sortName() {
      switch (this.sort) {
        case 'performance':
          return 'Rendement brut'
        case 'population':
          return "Nombre d'habitants"
        case 'pertinence':
        default:
          return 'Pertinence'
      }
    },
  },
  methods: {
    formatPagePath(page) {
      return { path: page === 1 ? '/ou-investir/' : `/ou-investir/page/${page}`, query: this.$route.query }
    },
    handleClick() {
      useTracking().track('Click CTA chasseur immo', {})
    },
    async handleSort(sort) {
      await navigateTo({
        path: '/ou-investir',
        query: { sort },
      })
    },
  },
}
</script>

<style lang="less" scoped>
:deep(.menu-item.active) {
  color: var(--ds-color-primary-100);
}

.cities {
  height: 100% !important;

  :deep(.section-container) {
    padding: 0;
  }

  .cities-container {
    .actions {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      column-gap: 24px;

      .sort-btn {
        position: relative;
      }

      @media @bp-sm {
        .sort-btn:before {
          position: absolute;
          content: '';
          height: 25px;
          top: 50%;
          transform: translateY(-50%);
          left: -12px;
          width: 1px;
          background-color: var(--ds-color-gray-25);
        }
      }
    }

    .separator {
      display: inline-block;
      margin: 20px 0;
      width: 100%;
      border-top: 1px solid var(--ds-color-gray-10);
    }

    .sell-ad-wrapper {
      grid-column: -2 / span 1;
      grid-row: 1;

      :deep(.uikit-ad) {
        height: 100%;

        .content {
          justify-content: center;
          gap: 30px;
        }

        @media @bp-xs {
          .content {
            margin: 17px;

            .title {
              font-size: var(--ds-font-sm);
            }

            .text {
              font-size: var(--ds-font-xs);
            }

            .uikit-btn {
              white-space: normal !important;
              height: unset !important;
              font-size: var(--ds-font-xs);
            }
          }
        }

        @media @bp-desktop {
          .content {
            .title {
              font-size: var(--ds-font-lg);
            }

            .text {
              font-size: 18px;
            }
          }
        }
      }
    }

    .banner-container {
      grid-row: 2;
      grid-column: ~'1 / -1';

      margin: 28px 0;
      border-radius: 8px;
      display: flex;
      background: var(--ds-color-primary-25);
      padding: 24px 12px;
      gap: 15px;
      justify-content: center;

      .banner {
        display: flex;
        flex-direction: column;
        gap: 20px;

        &-title {
          font-size: var(--ds-font-xl);
          span {
            color: var(--ds-color-primary-100);
          }
        }

        &-icon {
          color: var(--ds-color-primary-100);
        }

        @media @bp-desktop {
          text-align: center;
          max-width: 700px;
        }
      }

      .icon-mobile {
        display: block;
        @media @bp-desktop {
          display: none;
        }
      }

      .icon-desktop {
        display: none;
        @media @bp-desktop {
          margin-right: 10px;
          display: inline-block;
        }
      }
    }

    .cities-container__content {
      display: grid;
      grid-column-gap: 3%;
      grid-row-gap: 30px;
      grid-template-columns: repeat(1, 1fr);
      @media @bp-md {
        grid-template-columns: repeat(2, 1fr);
      }

      @media @bp-desktop {
        grid-template-columns: repeat(4, 1fr);
      }

      .city-skeleton {
        max-width: 450px;
        height: 246px;
      }
    }

    .cities-container__subheader {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      flex-wrap: wrap;

      .cities-container__subtitle {
        font-weight: var(--ds-weight-semi-bold);
        font-size: 26px;
        margin: 10px 0;

        span {
          color: var(--ds-color-primary-100);
        }
      }
    }

    .cities-container__cities-calculate {
      background: #f4f7fb;
      border: 2px solid #ffffff;
      border-radius: 5px;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      text-align: center;
      color: #272727;
      padding: 20px;
      margin: 28px 0;

      a {
        text-decoration: none;
        color: #272727;
      }
    }

    @media @bp-desktop {
      margin-top: 80px;

      .cities-container__subheader {
        flex-direction: row;

        .cities-container__subtitle {
          font-weight: var(--ds-weight-semi-bold);
          font-size: 34px;
        }
      }

      .cities-container__cities-calculate {
        flex-direction: row;
        padding: 10px;
      }
    }
  }

  .pagination {
    padding-top: 30px;
  }
}
</style>
